import { loadAddressingCountryMetadata } from '../generated/loaders';
import { AddressField, AddressingCountryCode } from '../generated/types';
import { toAddressingCountryCode } from './countries';

export type AddressFieldWithCountry = AddressField | 'COUNTRY';

export type AddressInput = {
    countryCode: AddressingCountryCode;
    addressLine1: string | null;
    addressLine2: string | null;
    administrativeArea: string | null;
    dependentLocality: string | null;
    locality: string | null;
    postalCode: string | null;
    sortingCode: string | null;
};

export type ExtendedAddressInput = AddressInput & {
    additionalName: string | null;
    familyName: string | null;
    givenName: string | null;
    organization: string | null;
};

export type AddressInputKey = keyof AddressInput;
export type ExtendedAddressInputKey = keyof ExtendedAddressInput;

/**
 * The country code used as the final fallback.
 */
export const DEFAULT_INPUT_COUNTRY_CODE = AddressingCountryCode.NL;

/**
 * Array of all possible address input fields.
 */
export const addressInputKeys: ReadonlyArray<AddressInputKey> = [
    'countryCode',
    'addressLine1',
    'addressLine2',
    'administrativeArea',
    'dependentLocality',
    'locality',
    'postalCode',
    'sortingCode',
];

export function isAddressInputKey(value: unknown): value is AddressInputKey {
    return typeof value === 'string' && addressInputKeys.includes(value as any);
}

/**
 * Array of address input keys from `ExtendedAddressInput` that are not in `AddressOnlyInput`.
 */
export const extendedAddressInputAdditionalKeys = [
    'additionalName',
    'familyName',
    'givenName',
    'organization',
] as const;

export function isExtendedAddressInputAdditionalKey(
    value: unknown
): value is keyof typeof extendedAddressInputAdditionalKeys {
    return (
        typeof value === 'string' &&
        extendedAddressInputAdditionalKeys.includes(value as any)
    );
}

/**
 * Array of all input properties of `ExtendedAddressInput`
 */
export const extendedAddressInputKeys: ReadonlyArray<ExtendedAddressInputKey> =
    [...extendedAddressInputAdditionalKeys, ...addressInputKeys];

export function isExtendedAddressInputKey(
    value: unknown
): value is ExtendedAddressInputKey {
    return (
        typeof value === 'string' &&
        extendedAddressInputKeys.includes(value as any)
    );
}

const addressFieldToExtendedAddressInputKey: Readonly<
    Record<AddressField, ExtendedAddressInputKey>
> = {
    [AddressField.ADDITIONAL_NAME]: 'additionalName',
    [AddressField.GIVEN_NAME]: 'givenName',
    [AddressField.FAMILY_NAME]: 'familyName',
    [AddressField.ADDRESS_LINE1]: 'addressLine1',
    [AddressField.ADDRESS_LINE2]: 'addressLine2',
    [AddressField.ADMINISTRATIVE_AREA]: 'administrativeArea',
    [AddressField.DEPENDENT_LOCALITY]: 'dependentLocality',
    [AddressField.LOCALITY]: 'locality',
    [AddressField.POSTAL_CODE]: 'postalCode',
    [AddressField.SORTING_CODE]: 'sortingCode',
    [AddressField.ORGANIZATION]: 'organization',
};

const addressInputKeyAutocompleteValue: Record<
    ExtendedAddressInputKey,
    string
> = {
    organization: 'organization',
    givenName: 'given-name',
    familyName: 'family-name',
    additionalName: 'additional-name',
    addressLine1: 'address-line1',
    addressLine2: 'address-line2',
    administrativeArea: 'address-level1',
    locality: 'address-level2',
    dependentLocality: 'address-level3',
    postalCode: 'postal-code',
    sortingCode: 'address-level4',
    countryCode: 'country',
};

export function getAddressFieldAutoCompleteValue(
    field: AddressFieldWithCountry | ExtendedAddressInputKey
): string {
    return addressInputKeyAutocompleteValue[toExtendedAddressInputKey(field)];
}

export function toExtendedAddressInputKey(
    field: AddressFieldWithCountry | ExtendedAddressInputKey
): ExtendedAddressInputKey {
    if (field === 'COUNTRY') return 'countryCode';
    return addressFieldToExtendedAddressInputKey[field] ?? field;
}

export function toAddressInputKey(
    field: AddressFieldWithCountry | ExtendedAddressInputKey
): AddressInputKey | undefined {
    const result = toExtendedAddressInputKey(field);
    if (!isAddressInputKey(result)) return undefined;
    return result;
}

export function getAddressFieldMaxLength(
    field: AddressFieldWithCountry | ExtendedAddressInputKey
): number {
    switch (toExtendedAddressInputKey(field)) {
        case 'countryCode':
            return 4;
        case 'sortingCode':
        case 'postalCode':
            return 63;
        default:
            return 255;
    }
}

export type AddressInputSource =
    | Readonly<
          Partial<
              Omit<AddressInput, 'countryCode'> & {
                  countryCode: string | null | undefined;
                  country: { code?: string | null | undefined };
              }
          >
      >
    | undefined
    | null;

export type ExtendedAddressInputSource =
    | Readonly<
          Partial<
              Omit<ExtendedAddressInput, 'countryCode'> & {
                  countryCode: string | null | undefined;
                  country: { code?: string | null | undefined };
              }
          >
      >
    | undefined
    | null;

function prepareStringInput(input: string | null | undefined): string | null {
    if (!input) return null;
    input = input.trim();
    if (input.length === 0) return null;
    return input;
}

export function getAddressCountryCode(
    source?: unknown,
    defaultCountryCode?: AddressingCountryCode | string
): AddressingCountryCode {
    const countryCode =
        toAddressingCountryCode(defaultCountryCode) ??
        DEFAULT_INPUT_COUNTRY_CODE;

    if (!source) return countryCode;

    if (typeof source === 'string') {
        return toAddressingCountryCode(source) ?? countryCode;
    }

    if (typeof source !== 'object' || !Array.isArray(source)) {
        return countryCode;
    }

    if ('countryCode' in source && source.countryCode === 'string') {
        const candidate = toAddressingCountryCode(source.countryCode);
        if (candidate) return candidate;
    }

    if (
        'country' in source &&
        !!source.country &&
        typeof source.country === 'object' &&
        'code' in source.country &&
        typeof source.country.code === 'string'
    ) {
        const candidate = toAddressingCountryCode(source.country.code);
        if (candidate) return candidate;
    }

    if ('code' in source && typeof source.code === 'string') {
        const candidate = toAddressingCountryCode(source.code);
        if (candidate) return candidate;
    }

    return countryCode;
}

export function createAddressInput(
    source?: AddressInputSource,
    defaultCountryCode?: AddressingCountryCode | string
): AddressInput {
    source ??= {};

    return {
        countryCode: getAddressCountryCode(source, defaultCountryCode),
        addressLine1: prepareStringInput(source.addressLine1),
        addressLine2: prepareStringInput(source.addressLine2),
        administrativeArea: prepareStringInput(source.administrativeArea),
        dependentLocality: prepareStringInput(source.dependentLocality),
        locality: prepareStringInput(source.locality),
        postalCode: prepareStringInput(source.postalCode),
        sortingCode: prepareStringInput(source.sortingCode),
    };
}

/**
 * Updates the provided `address` using `source`.
 *
 * Returns `true` when the value of `address` actually changed.
 *
 * This function is mainly useful in frameworks like VueJS to prevent infinite reccursion problems.
 */
export function updateAddressInput(
    address: AddressInput,
    source?: AddressInputSource
): boolean {
    let changed = false;
    if (!source) return changed;

    const newCountryCode = toAddressingCountryCode(
        source.countryCode ?? source.country?.code
    );
    if (newCountryCode && address.countryCode !== newCountryCode) {
        address.countryCode = newCountryCode;
        changed = true;
    }

    for (const field of addressInputKeys) {
        if (field !== 'countryCode' && source[field] !== undefined) {
            const newValue = prepareStringInput(source[field]);
            if (address[field] !== newValue) {
                address[field] = newValue;
                changed = true;
            }
        }
    }

    return changed;
}

export function createExtendedAddressInput(
    source?: ExtendedAddressInputSource,
    defaultCountryCode?: AddressingCountryCode | string
): ExtendedAddressInput {
    source ??= {};
    return {
        organization: prepareStringInput(source.organization),
        givenName: prepareStringInput(source.givenName),
        familyName: prepareStringInput(source.familyName),
        additionalName: prepareStringInput(source.additionalName),
        ...createAddressInput(source, defaultCountryCode),
    };
}

export function updateExtendedAddressInput(
    address: ExtendedAddressInput,
    source?: ExtendedAddressInputSource
): boolean {
    if (!source) return false;
    let changed = updateAddressInput(address, source);

    for (const field of extendedAddressInputAdditionalKeys) {
        if (source[field] !== undefined) {
            const newValue = prepareStringInput(source[field]);
            if (address[field] !== newValue) {
                address[field] = newValue;
                changed = true;
            }
        }
    }

    return changed;
}

export function trimAddressInput(
    source: AddressInputSource,
    usedFields: ReadonlyArray<AddressField | ExtendedAddressInputKey>,
    defaultCountryCode?: AddressingCountryCode | string
): AddressInput {
    const usedKeys = usedFields.map(toAddressInputKey);

    const unusedKeys = addressInputKeys.filter(
        (x): x is Exclude<AddressInputKey, 'countryCode'> =>
            !usedKeys.includes(x) && x !== 'countryCode'
    );

    const result = createAddressInput(source, defaultCountryCode);
    for (const unusedKey of unusedKeys) {
        result[unusedKey] = null;
    }
    return result;
}

export function trimExtendedAddressInput(
    source: ExtendedAddressInputSource,
    usedFields: ReadonlyArray<AddressField | ExtendedAddressInputKey>,
    defaultCountryCode?: AddressingCountryCode | string
): AddressInput {
    const usedKeys = usedFields.map(toExtendedAddressInputKey);

    const unusedKeys = extendedAddressInputKeys.filter(
        (x): x is Exclude<ExtendedAddressInputKey, 'countryCode'> =>
            !usedKeys.includes(x) && x !== 'countryCode'
    );

    const result = createExtendedAddressInput(source, defaultCountryCode);
    for (const unusedKey of unusedKeys) {
        result[unusedKey] = null;
    }
    return result;
}

export async function prepareAddressInput(
    source: AddressInputSource,
    defaultCountryCode?: AddressingCountryCode | string
): Promise<AddressInput> {
    const countryCode = getAddressCountryCode(source, defaultCountryCode);
    const countries = await loadAddressingCountryMetadata();
    const country = countries[countryCode];
    const usedFields = country.addressFormat.usedFields;
    return trimAddressInput(source, usedFields, defaultCountryCode);
}

export async function prepareExtendedAddressInput(
    source: ExtendedAddressInputSource,
    defaultCountryCode?: AddressingCountryCode | string
): Promise<AddressInput> {
    const countryCode = getAddressCountryCode(source, defaultCountryCode);
    const countries = await loadAddressingCountryMetadata();
    const country = countries[countryCode];
    const usedFields = country.addressFormat.usedFields;
    return trimExtendedAddressInput(source, usedFields, defaultCountryCode);
}
